import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import InputText from '../Input/InputText';
import StatusIcon from '../StatusIcon/StatusIcon';
import { getAccountDetails } from '../../service/DealershipAccount.service';
import useErrorProneRequest from '../../hooks/useErrorProneRequest';

const Status = {
  initial: 'initial',
  inprogress: 'inprogress',
  success: 'success',
  failure: 'failure'
};

const isAccountValid = (value) => {
  return value?.id && value?.name !== undefined;
};

const DealershipAccountField = ({
  label,
  id,
  value,
  onChange,
  required = false,
  maxLength,
  disabled = false,
  dataTest,
  placeholder,
  error,
  bottomMarginSizing
}) => {
  const [dealershipId, setDealershipId] = useState('');
  const [status, setStatus] = useState(Status.initial);
  const [, , executeRequest] = useErrorProneRequest();

  const getDealershipAccount = useCallback(
    async (accountId) => {
      executeRequest(
        async () => {
          setStatus(Status.inprogress);

          const dealershipAccount = await getAccountDetails({ accountId });

          onChange(dealershipAccount);
          setStatus(Status.success);
        },
        () => {
          onChange({ id: accountId });
          setStatus(Status.failure);
        }
      );
    },
    [executeRequest, onChange]
  );

  // "static" initialization effect goes first
  useEffect(() => {
    if (isAccountValid(value)) {
      setDealershipId(value.id);
      setStatus(Status.success);
      return;
    }

    if (!value?.id) {
      setDealershipId('');
      setStatus(Status.initial);
      return;
    }
  }, [value]);

  // separate effect for the async initialization to avoid collision with "static" updates on status changes
  useEffect(() => {
    if (status !== Status.initial || isAccountValid(value) || !value?.id) {
      return;
    }

    setDealershipId(value.id);
    getDealershipAccount(value.id);
  }, [value, status, getDealershipAccount]);

  const handleBlur = () => {
    if (dealershipId === value?.id) {
      return;
    }

    if (!dealershipId) {
      onChange({ id: '' });
      setStatus(Status.initial);
      return;
    }

    getDealershipAccount(dealershipId);
  };

  return (
    <InputText
      label={label}
      id={id}
      value={dealershipId}
      onChange={(e) => setDealershipId(e.target.value)}
      onBlur={handleBlur}
      required={required}
      maxLength={maxLength}
      disabled={disabled}
      dataTest={dataTest}
      placeholder={placeholder}
      error={error}
      bottomMarginSizing={bottomMarginSizing}
      icon={
        [Status.inprogress, Status.success].includes(status) ? (
          <StatusIcon status={status} dataTest="dealership-account-status-icon" />
        ) : null
      }
    />
  );
};

DealershipAccountField.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string,
  value: PropTypes.object,
  onChange: PropTypes.func,
  dataTest: PropTypes.string.isRequired,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.string,
  bottomMarginSizing: PropTypes.oneOf([1, 0, undefined])
};

export default DealershipAccountField;
